import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BriefcaseBusiness, Check, Sparkles, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type OnboardingProductKey = "recruit" | "leads" | "intelligence";

const getOptions = (
  t: (key: string) => string,
): {
  id: OnboardingProductKey;
  title: string;
  description: string;
  icon: typeof BriefcaseBusiness;
}[] => [
  {
    id: "recruit",
    title: t("products.recruit.title"),
    description: t("products.recruit.description"),
    icon: BriefcaseBusiness,
  },
  {
    id: "leads",
    title: t("products.leads.title"),
    description: t("products.leads.description"),
    icon: Target,
  },
  {
    id: "intelligence",
    title: t("products.intelligence.title"),
    description: t("products.intelligence.description"),
    icon: Sparkles,
  },
];

interface SelectProductStepProps {
  onSubmit: (products: OnboardingProductKey[]) => void;
  errorMsg: string | null;
  loading: boolean;
}

/**
 * Final onboarding step: registers which products the business is interested
 * in. Activation is sales-led — the selection is a request the Graftus team
 * fulfils, not an immediate unlock.
 */
export default function SelectProductStep({
  onSubmit,
  errorMsg,
  loading,
}: SelectProductStepProps) {
  const { t } = useTranslation("onboarding");
  const [selected, setSelected] = useState<Set<OnboardingProductKey>>(
    new Set(),
  );
  const options = getOptions(t);

  const toggle = (id: OnboardingProductKey) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <p className="text-[15px] leading-relaxed text-muted-foreground">
        {t("selectProducts.description")}
      </p>

      {errorMsg && (
        <div
          role="alert"
          className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm font-semibold text-destructive"
        >
          {errorMsg}
        </div>
      )}

      <div className="grid gap-3">
        {options.map((opt) => {
          const Icon = opt.icon;
          const isActive = selected.has(opt.id);
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => toggle(opt.id)}
              aria-pressed={isActive}
              className={cn(
                "flex w-full gap-4 rounded-xl border bg-card p-4 text-left transition-colors",
                "hover:border-primary/50 hover:bg-muted/40",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                isActive
                  ? "border-primary ring-1 ring-primary/30 bg-primary/5"
                  : "border-border",
              )}
            >
              <div
                className={cn(
                  "flex h-11 w-11 shrink-0 items-center justify-center rounded-lg",
                  isActive
                    ? "bg-primary/15 text-primary"
                    : "bg-muted text-muted-foreground",
                )}
              >
                <Icon className="h-5 w-5" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-foreground">{opt.title}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {opt.description}
                </p>
              </div>
              <div
                className={cn(
                  "flex h-5 w-5 shrink-0 items-center justify-center self-center rounded-full border",
                  isActive
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border",
                )}
              >
                {isActive && <Check className="h-3.5 w-3.5" />}
              </div>
            </button>
          );
        })}
      </div>

      <Button
        type="button"
        disabled={selected.size === 0 || loading}
        className="h-11 w-full bg-foreground text-background hover:bg-foreground/90"
        onClick={() => onSubmit([...selected])}
      >
        {loading
          ? t("selectProducts.submitting")
          : t("selectProducts.submit")}
      </Button>
    </div>
  );
}
